/*
 * ===========================================================================================================
 * 📡 useNetworkWatcher.ts
 * @description Composable reactivo que detecta cambios en la conexión de red en tiempo real.
 *              Integra eventos del navegador y un "ping" periódico al servidor para mayor fiabilidad.
 * ===========================================================================================================
 */

import { ref, onMounted, onUnmounted } from "vue";
import { checkConnection } from "@/services/apiServices/checkConnection"; // 💡 Ping real a internet

// 🌐 Estado reactivo global que indica si el dispositivo está online (basado en verificación real)
const isOnline = ref(navigator.onLine);

/**
 * 🔧 useNetworkWatcher
 *
 * 💡 Este composable:
 *    - Actualiza `isOnline` en función del estado real de red.
 *    - Escucha eventos `online` y `offline` del navegador.
 *    - Hace un ping cada 5 segundos (útil para detectar desconexión física como WiFi apagada).
 *
 * @returns { isOnline, checkConnection } - Estado de red reactivo + función de chequeo directo.
 */
export function useNetworkWatcher() {
  /**
   * 🔁 updateOnlineStatus
   * Ejecuta `checkConnection` y actualiza `isOnline`
   */
  const updateOnlineStatus = async () => {
    const online = await checkConnection(); // ✅ Realiza ping real
    isOnline.value = online;
  };

  /**
   * 🚦 startMonitoring
   * Configura los listeners y el interval para detectar cambios de red
   */
  const startMonitoring = () => {
    // 🎧 Escucha eventos de red nativos del navegador
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    // 🧹 Limpieza automática al desmontar
    onUnmounted(() => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    });
  };

  // 🚀 Al montar el componente, iniciamos la primera comprobación y los listeners
  onMounted(() => {
    updateOnlineStatus();
    startMonitoring();
  });

  return {
    isOnline, // 📡 Estado reactivo de conexión
    checkConnection, // 🔍 Función exportada por si se necesita una verificación directa
  };
}
